'use client'

import { motion } from "framer-motion"

/**
 * OrnamentalDivider — frise orientale en or avec motif central.
 * Sert de seuil symbolique entre deux sections majeures.
 *
 * Variantes:
 * - "default" : losange central + deux filets or
 * - "knot"    : nœud ottoman avec cercle central
 * - "star"    : étoile à huit branches (style zellige)
 */
export function OrnamentalDivider({
  variant = "default",
  className = "",
}: {
  variant?: "default" | "knot" | "star"
  className?: string
}) {
  const mark = {
    default: (
      <svg viewBox="0 0 36 36" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="0.9">
        <path d="M18 1 L35 18 L18 35 L1 18 Z" />
        <path d="M18 6 L30 18 L18 30 L6 18 Z" />
        <circle cx="18" cy="18" r="2.5" fill="currentColor" fillOpacity="0.55" />
      </svg>
    ),
    knot: (
      <svg viewBox="0 0 36 36" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="0.9">
        <path d="M18 4 C26 4 32 10 32 18 C32 26 26 32 18 32 C10 32 4 26 4 18 C4 10 10 4 18 4 Z" />
        <path d="M18 9 C23 9 27 13 27 18 C27 23 23 27 18 27 C13 27 9 23 9 18 C9 13 13 9 18 9 Z" />
        <path d="M18 1 L18 9 M18 27 L18 35 M1 18 L9 18 M27 18 L35 18" />
        <circle cx="18" cy="18" r="1.8" fill="currentColor" fillOpacity="0.7" />
      </svg>
    ),
    star: (
      <svg viewBox="0 0 36 36" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="0.9">
        <path d="M18 1 L23 13 L35 14 L26 22 L29 34 L18 28 L7 34 L10 22 L1 14 L13 13 Z" />
        <circle cx="18" cy="18" r="3" fill="currentColor" fillOpacity="0.4" />
        <circle cx="18" cy="18" r="1.5" fill="currentColor" />
      </svg>
    ),
  }[variant]

  return (
    <div className={`ornament-divider text-gold ${className}`}>
      <motion.span
        aria-hidden
        initial={{ scaleX: 0, opacity: 0 }}
        whileInView={{ scaleX: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{ transformOrigin: "right center" }}
        className="flex-1 h-px bg-gradient-to-l from-gold/60 via-gold/40 to-transparent"
      />
      <motion.span
        aria-hidden
        initial={{ scale: 0.6, opacity: 0, rotate: -45 }}
        whileInView={{ scale: 1, opacity: 1, rotate: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex-shrink-0 flex items-center justify-center"
      >
        {mark}
      </motion.span>
      <motion.span
        aria-hidden
        initial={{ scaleX: 0, opacity: 0 }}
        whileInView={{ scaleX: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.05 }}
        style={{ transformOrigin: "left center" }}
        className="flex-1 h-px bg-gradient-to-r from-gold/60 via-gold/40 to-transparent"
      />
    </div>
  )
}
